import {
  SyncGetChangeOutRequest,
  SyncUndercarriageChangeOutRequest,
} from '../api-contracts';
import { ObservableItemIdentifier } from '../api-contracts/other/ObservableItemIdentifier';
import {
  EquipmentModel,
  EquipmentPartModel,
  ObservationModel,
} from '../data/types';

/**
 * Serialises the operating-hours screen's undercarriage change-out capture into the typed
 * sync contract (#10223 native adoption). The capture itself stays observation-based (the
 * screen is unchanged); at submit time the checked side families become typed statements
 * the server ingests directly - the server no longer distils observations.
 */

const asLocalWireDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * The wire format is a plain yyyy-MM-dd string carrying the inspector's LOCAL calendar day.
 * The DateField stores the picked day as local midnight serialised to UTC ("2026-08-19
 * 14:00:00.000Z" when the inspector picked 20 Aug in AEST), so the UTC leading token is a
 * day early for any positive offset - the timestamp must be parsed and rendered in device
 * local time, which is the capture's own timezone. Plain date-only values (older app
 * builds) pass through literally.
 */
export const toChangeOutWireDate = (
  raw: string | undefined | null,
): string | undefined => {
  if (!raw) return undefined;
  const leading = raw.split(/[ T]/)[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(leading)) return undefined;
  if (leading === raw.trim()) return leading;
  const parsed = new Date(raw.replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? leading : asLocalWireDate(parsed);
};

export const buildUndercarriageChangeOutSyncItems = ({
  observations,
  crawlerSides,
}: {
  observations: ObservationModel[];
  crawlerSides: EquipmentModel[];
}): SyncUndercarriageChangeOutRequest[] => {
  const { UndercarriageChangeOut } =
    ObservableItemIdentifier.Identifiers.Operation;
  const items: SyncUndercarriageChangeOutRequest[] = [];

  for (const sideName of ['Left', 'Right'] as const) {
    const paths = UndercarriageChangeOut[sideName];
    const valueOf = (path: string) =>
      observations.find((o) => o.observableItem.identifier === path)?.value;

    if (valueOf(paths.Checked) !== 'true') {
      continue;
    }
    const changeOutDate = toChangeOutWireDate(valueOf(paths.Date));
    if (!changeOutDate) {
      continue;
    }
    const side = crawlerSides.find((equipment) =>
      equipment.name?.toLowerCase().startsWith(sideName.toLowerCase()),
    );
    if (!side) {
      continue;
    }

    const hoursRaw = valueOf(paths.MachineHoursAtChange);
    const hours =
      hoursRaw !== undefined && hoursRaw !== '' ? parseFloat(hoursRaw) : NaN;

    items.push({
      externalId: crypto.randomUUID(),
      sideEquipmentExternalId: side.externalId,
      changeOutDate,
      machineHoursAtChangeOut: Number.isNaN(hours) ? null : hours,
      notes: '',
    });
  }

  return items;
};

/**
 * Serialises the per-position GET change-out capture (Bucket.GroundEngagingTools.ChangeOut.*
 * observations at equipment-part level) into typed statements. A statement exists for a
 * position when a positive total is declared; reasons and specification enrich it. The
 * capture has no date field - the change is recorded during this inspection, so the
 * inspection date is the change-out date. Reason checkboxes are booleans on the device,
 * so a checked reason travels as count 1.
 */
export const buildGetChangeOutSyncItems = ({
  observations,
  equipmentPartsByExternalId,
  partIdByExternalId,
  inspectionDate,
}: {
  observations: ObservationModel[];
  equipmentPartsByExternalId: Map<string, EquipmentPartModel>;
  partIdByExternalId: Map<string, number>;
  inspectionDate: Date | string;
}): SyncGetChangeOutRequest[] => {
  const { ChangeOut } =
    ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools;
  // The inspector recorded the change on the inspection's LOCAL day - toISOString would
  // shift an AEST evening capture back a UTC day, so format from local date components.
  const changeOutDate =
    inspectionDate instanceof Date
      ? asLocalWireDate(inspectionDate)
      : toChangeOutWireDate(String(inspectionDate));
  if (!changeOutDate) {
    return [];
  }

  const byPosition = new Map<string, ObservationModel[]>();
  for (const observation of observations) {
    if (
      !observation.observableItem.identifier.startsWith(
        'Bucket.GroundEngagingTools.ChangeOut.',
      )
    ) {
      continue;
    }
    if (!observation.entityExternalId) {
      continue;
    }
    const group = byPosition.get(observation.entityExternalId) ?? [];
    group.push(observation);
    byPosition.set(observation.entityExternalId, group);
  }

  const items: SyncGetChangeOutRequest[] = [];
  for (const [equipmentPartExternalId, group] of byPosition) {
    const equipmentPart = equipmentPartsByExternalId.get(
      equipmentPartExternalId,
    );
    if (!equipmentPart || !equipmentPart.externalEquipmentId) {
      continue;
    }
    const position = equipmentPart.position ?? -1;
    if (position < 1) {
      continue;
    }

    const valueOf = (path: string) =>
      group.find((o) => o.observableItem.identifier === path)?.value;
    const countOf = (path: string): number | null => {
      const raw = valueOf(path);
      if (raw === undefined || raw === '') return null;
      const parsed = parseInt(raw, 10);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const partsChanged = countOf(ChangeOut.Position.TotalParts);
    const pinsChanged = countOf(ChangeOut.Position.TotalPins);
    const basesChanged = countOf(ChangeOut.Position.TotalBases);
    const declaredChange =
      (partsChanged ?? 0) > 0 ||
      (pinsChanged ?? 0) > 0 ||
      (basesChanged ?? 0) > 0;
    if (!declaredChange) {
      continue;
    }

    const newPartsChecked =
      valueOf(ChangeOut.Specification.NewParts.Checked) === 'true';
    const samePartsChecked =
      valueOf(ChangeOut.Specification.SameParts.Checked) === 'true';
    const newPartExternalId = valueOf(ChangeOut.Specification.PartId);
    const partHoursRaw = valueOf(ChangeOut.PartHours);
    const partHours =
      partHoursRaw !== undefined && partHoursRaw !== ''
        ? parseFloat(partHoursRaw)
        : NaN;

    items.push({
      externalId: crypto.randomUUID(),
      bucketEquipmentExternalId: equipmentPart.externalEquipmentId,
      positionNumber: position,
      changeOutDate,
      partsChanged,
      pinsChanged,
      basesChanged,
      wornCount: valueOf(ChangeOut.Reason.Worn.Checked) === 'true' ? 1 : null,
      damagedCount:
        valueOf(ChangeOut.Reason.Damaged.Checked) === 'true' ? 1 : null,
      missingCount:
        valueOf(ChangeOut.Reason.Missing.Checked) === 'true' ? 1 : null,
      newPartId: newPartExternalId
        ? (partIdByExternalId.get(newPartExternalId) ?? null)
        : null,
      isNewSpec: newPartsChecked ? true : samePartsChecked ? false : null,
      partHoursAtChange: Number.isNaN(partHours) ? null : partHours,
      notes: '',
    });
  }

  return items;
};
